import {
  AutocallConsolationGenerator,
  AutocallConsolationOptions,
} from '@/pages/instruments/new-instrument/schedule-builders/autocall-consolation'
import {
  AutocallOptions,
  AutocallStandardGenerator,
} from '@/pages/instruments/new-instrument/schedule-builders/autocall-standard'
import {
  DigitalGenerator,
  DigitalOptions,
} from '@/pages/instruments/new-instrument/schedule-builders/digital'
import {
  PhoenixNonMemoryGenerator,
  PhoenixNonMemoryOptions,
} from '@/pages/instruments/new-instrument/schedule-builders/phoenix-non-memory'
import {
  RangeAccrualGenerator,
  RangeAccrualOptions,
} from '@/pages/instruments/new-instrument/schedule-builders/range-accrual'
import {
  SupertrackerGenerator,
  SupertrackerOptions,
} from '@/pages/instruments/new-instrument/schedule-builders/supertracker'
import { VanillaGenerator } from '@/pages/instruments/new-instrument/schedule-builders/vanilla'
import { VanillaDigitalGenerator } from '@/pages/instruments/new-instrument/schedule-builders/vanilla-digital'
import {
  VanillaSpreadGenerator,
  VanillaSpreadOptions,
} from '@/pages/instruments/new-instrument/schedule-builders/vanilla-spread'
import {
  VanillaStructuredGenerator,
  VanillaStructuredOptions,
} from '@/pages/instruments/new-instrument/schedule-builders/vanilla-structured'
import { AllInstrumentsSchemaType } from '@/pages/instruments/new-instrument/schemas'
import { AutocallConsolationSchemaType } from '@/pages/instruments/new-instrument/schemas/autocall-consolation.schema'
import { AutocallStandardSchemaType } from '@/pages/instruments/new-instrument/schemas/autocall-standard.schema'
import { DigitalSchemaType } from '@/pages/instruments/new-instrument/schemas/digital.schema'
import { PhoenixNonMemorySchemaType } from '@/pages/instruments/new-instrument/schemas/phoenix-non-memory.schema'
import { RangeAccrualSchemaType } from '@/pages/instruments/new-instrument/schemas/range-accrual.schema'
import { SupertrackerSchemaType } from '@/pages/instruments/new-instrument/schemas/supertracker.schema'
import { VanillaDigitalSchemaType } from '@/pages/instruments/new-instrument/schemas/vanilla-digital.schema'
import { VanillaSpreadSchemaType } from '@/pages/instruments/new-instrument/schemas/vanilla-spread.schema'
import { VanillaStructuredSchemaType } from '@/pages/instruments/new-instrument/schemas/vanilla-structured.schema'
import { VanillaSchemaType } from '@/pages/instruments/new-instrument/schemas/vanilla.schema'

export const getScheduleLegsFromFormValues = (values: AllInstrumentsSchemaType) => {
  switch (values?.instrument_type) {
    case 'autocall-standard':
      return AutocallStandardGenerator.generate(mapAutocallSchemaToOptions(values))
    case 'autocall-consolation':
      return AutocallConsolationGenerator.generate(mapAutocallConsolationSchemaToOptions(values))
    case 'autocall-phoenix':
      return PhoenixNonMemoryGenerator.generate(mapPhoenixNonMemorySchemaToOptions(values))
    case 'digital':
      return DigitalGenerator.generate(mapDigitalSchemaToOptions(values))
    case 'vanilla-spread':
      return VanillaSpreadGenerator.generate(mapCallPutSpreadSchemaToOptions(values))
    case 'vanilla-structured':
      return VanillaStructuredGenerator.generate(mapCallPutStructuredSchemaToOptions(values))
    case 'range-accrual':
      return RangeAccrualGenerator.generate(mapRangeAccrualSchemaToOptions(values))
    case 'supertracker':
      return SupertrackerGenerator.generate(mapSupertrackerSchemaToOptions(values))
    case 'vanilla':
      return VanillaGenerator.generate(mapVanillaSchemaToOptions(values))
    case 'vanilla-digital':
      return VanillaDigitalGenerator.generate(mapVanillaDigitalSchemaToOptions(values))
    default:
      return []
  }
}

const mapDigitalSchemaToOptions = (values: DigitalSchemaType): DigitalOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapVanillaDigitalSchemaToOptions = (values: VanillaDigitalSchemaType): DigitalOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapVanillaSchemaToOptions = (values: VanillaSchemaType): DigitalOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapSupertrackerSchemaToOptions = (values: SupertrackerSchemaType): SupertrackerOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapRangeAccrualSchemaToOptions = (values: RangeAccrualSchemaType): RangeAccrualOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapCallPutStructuredSchemaToOptions = (
  values: VanillaStructuredSchemaType,
): VanillaStructuredOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapCallPutSpreadSchemaToOptions = (values: VanillaSpreadSchemaType): VanillaSpreadOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapAutocallSchemaToOptions = (values: AutocallStandardSchemaType): AutocallOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapPhoenixNonMemorySchemaToOptions = (
  values: PhoenixNonMemorySchemaType,
): PhoenixNonMemoryOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}

const mapAutocallConsolationSchemaToOptions = (
  values: AutocallConsolationSchemaType,
): AutocallConsolationOptions => {
  return {
    ...values,
    initial_observation_date: new Date(values.initial_observation_date),
    maturity_date: new Date(values.maturity_date),
    // @ts-expect-error
    strikes: values?.underlyings ?? [],
  }
}
